import { darken, rgba } from 'polished';
import styled from 'styled-components';

import {
  UI_SHAPE,
  UiShape,
} from '@savgroup-front-common/constants/src/shared/shape';
import {
  UI_SIZE,
  UiSize,
} from '@savgroup-front-common/constants/src/shared/size';
import { DefaultStyledProps } from '@savgroup-front-common/types';

import { rem } from '../../helpers';
import { getThemeFromProps } from '../../theme/helpers';

import {
  TAG_TEXT_TRANSFORM,
  TAG_VARIANT,
  TagTextTransform,
  TagVariant,
} from './Tag.types';

export type { TagTextTransform, TagVariant } from './Tag.types';

type InternalVariant = TagVariant | 'disabled';

type ColorProps = {
  $variant?: TagVariant;
  $isNewUiEnabled?: boolean;
  theme?: DefaultStyledProps['theme'];
  disabled?: boolean;
  primary?: boolean;
  secondary?: boolean;
  success?: boolean;
  alert?: boolean;
  danger?: boolean;
  neutral?: boolean;
};

const legacyVariant = (props: ColorProps): TagVariant => {
  if (props.$variant) {
    return props.$variant;
  }
  if (props.primary) {
    return TAG_VARIANT.PRIMARY;
  }
  if (props.secondary) {
    return TAG_VARIANT.SECONDARY;
  }
  if (props.success) {
    return TAG_VARIANT.SUCCESS;
  }
  if (props.danger) {
    return TAG_VARIANT.DANGER;
  }
  if (props.alert) {
    return TAG_VARIANT.ALERT;
  }
  if (props.neutral) {
    return TAG_VARIANT.NEUTRAL;
  }

  return TAG_VARIANT.DEFAULT;
};

const resolveColors = (props: ColorProps) => {
  const theme = getThemeFromProps(props);

  let variant: InternalVariant = legacyVariant(props);

  if (props.disabled) {
    variant = 'disabled';
  }

  switch (variant) {
    case 'disabled':
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.disabled.bgColor,
          color: theme.newUI.tagsColors?.disabled.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.disabled, 0.4),
        color: theme.colors.paragraphTextColor,
      };
    case TAG_VARIANT.NEUTRAL:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.neutral.bgColor,
          color: theme.newUI.tagsColors?.neutral.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.disabled, 0.8),
        color: theme.colors.darkBackground,
      };
    case TAG_VARIANT.DANGER:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.danger.bgColor,
          color: theme.newUI.tagsColors?.danger.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.danger, 0.1),
        color: theme.colors.danger,
      };
    case TAG_VARIANT.SUCCESS:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.success.bgColor,
          color: theme.newUI.tagsColors?.success.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.success, 0.1),
        color: theme.colors.success,
      };
    case TAG_VARIANT.PRIMARY:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.primary.bgColor,
          color: theme.newUI.tagsColors?.primary.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.primary, 0.1),
        color: theme.colors.primary,
      };
    case TAG_VARIANT.SECONDARY:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.secondary.bgColor,
          color: theme.newUI.tagsColors?.secondary.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.secondary, 0.1),
        color: theme.colors.secondary,
      };
    case TAG_VARIANT.DEFAULT:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: theme.newUI.tagsColors?.alert.bgColor,
          color: theme.newUI.tagsColors?.alert.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.alert, 0.2),
        color: theme.colors.alert,
      };
    case TAG_VARIANT.ALERT:
    default:
      if (props.$isNewUiEnabled) {
        return {
          bgColor: props.theme?.newUI.tagsColors?.alert.bgColor,
          color: theme.newUI.tagsColors?.alert.color,
        };
      }

      return {
        bgColor: rgba(theme.colors.alert, 0.1),
        color: theme.colors.alert,
      };
  }
};

export const backgroundColorBasedOnProps = (props: ColorProps) =>
  resolveColors(props).bgColor;

export const colorBasedOnProps = (props: ColorProps) =>
  resolveColors(props).color;

const SIZE_STYLES: Partial<
  Record<
    UiSize,
    {
      padding?: string;
      fontSize?: string;
      maxHeight?: string;
      height?: string;
      lineHeight?: number;
    }
  >
> = {
  [UI_SIZE.XS]: {
    padding: `${rem(4)} ${rem(6)}`,
    fontSize: rem(10),
    maxHeight: rem(18),
    height: rem(18),
    lineHeight: 1.4,
  },
  [UI_SIZE.SM]: {
    padding: `${rem(4)} ${rem(10)}`,
    fontSize: rem(10),
    maxHeight: rem(32),
    height: rem(28),
    lineHeight: 1.45,
  },
  [UI_SIZE.MD]: {
    padding: `${rem(4)} ${rem(10)}`,
    fontSize: rem(12),
    maxHeight: rem(32),
    height: undefined,
    lineHeight: 1.5,
  },
};

interface $TagProps extends DefaultStyledProps {
  disabled?: boolean;
  withoutMargin?: boolean;
  $noEllispis?: boolean;
  $fluid?: boolean;
  $isNaked: boolean;
  $size: UiSize;
  $variant: TagVariant;
  $shape: UiShape;
  $isNewUiEnabled?: boolean;
  $textTransform: TagTextTransform;
  onClick?: () => void;
  $isHollow?: boolean;
  hasZIndex?: boolean;
}

export const $Tag = styled.div<$TagProps>`
  width: ${({ $fluid }) => ($fluid ? null : 'fit-content')};
  min-width: 0;
  max-height: ${({ $noEllispis, $size }) =>
    $noEllispis ? null : SIZE_STYLES[$size]?.maxHeight};
  padding: ${({ $isNaked, $size }) =>
    $isNaked ? `${rem(4)} 0` : SIZE_STYLES[$size]?.padding};
  margin: 0;
  gap: ${rem(8)};
  margin-bottom: ${({ withoutMargin }) => (withoutMargin ? null : rem(8))};
  font-size: ${({ $size }) => SIZE_STYLES[$size]?.fontSize};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  display: inline-flex;
  white-space: ${({ $noEllispis }) => ($noEllispis ? null : 'nowrap')};
  overflow: ${({ $noEllispis }) => ($noEllispis ? null : 'hidden')};
  text-overflow: ${({ $noEllispis }) => ($noEllispis ? null : 'ellipsis')};
  font-style: normal;
  text-align: ${({ $noEllispis }) => ($noEllispis ? 'left' : 'center')};
  border: 1px solid;
  z-index: ${({ hasZIndex }) => (hasZIndex ? 20 : 'auto')};
  border-color: ${({ theme, $isHollow, $isNewUiEnabled, ...rest }) =>
    $isHollow
      ? backgroundColorBasedOnProps({ theme, $isNewUiEnabled, ...rest })
      : 'transparent'};
  background-color: ${({
    theme,
    $isHollow,
    $isNaked,
    $isNewUiEnabled,
    ...rest
  }) => {
    if ($isHollow || $isNaked) {
      return 'transparent';
    }

    return backgroundColorBasedOnProps({ theme, $isNewUiEnabled, ...rest });
  }};
  border-radius: ${({ theme, $shape }) =>
    $shape === UI_SHAPE.ROUND ? rem(999) : theme.borders.radius};

  &:hover {
    cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
    background-color: ${({
      theme,
      onClick,
      $isNaked,
      $isNewUiEnabled,
      ...rest
    }) => {
      if (onClick) {
        const color = $isNaked
          ? 'transparent'
          : backgroundColorBasedOnProps({ theme, $isNewUiEnabled, ...rest });

        return darken(0.08, color);
      }

      return undefined;
    }};
  }

  color: ${colorBasedOnProps};
  transition-duration: 0.3s;

  ${({ $textTransform }) => {
    const transform = $textTransform ?? TAG_TEXT_TRANSFORM.UPPERCASE;

    if (transform === TAG_TEXT_TRANSFORM.UPPERFIRST) {
      return `
        text-transform: none;
        & > span:first-child::first-letter { text-transform: uppercase; }
      `;
    }

    return `text-transform: ${transform};`;
  }};
  line-height: ${({ $size }) => SIZE_STYLES[$size]?.lineHeight};
  align-items: center;
  position: relative;
  height: ${({ $size }) => SIZE_STYLES[$size]?.height};

  & > span {
    overflow: ${({ $noEllispis }) => ($noEllispis ? 'visible' : 'hidden')};
    text-overflow: ${({ $noEllispis }) => ($noEllispis ? 'clip' : 'ellipsis')};
    white-space: ${({ $noEllispis }) => ($noEllispis ? 'normal' : 'nowrap')};
    overflow-wrap: ${({ $noEllispis }) =>
      $noEllispis ? 'anywhere' : 'normal'};
    word-break: ${({ $noEllispis }) => ($noEllispis ? 'break-word' : 'normal')};
  }
`;

export const $TagCloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

export const $TagContent = styled.span<{
  $isClosable?: boolean;
  onClick?: (event: React.MouseEvent) => void;
}>`
  display: block;
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  justify-content: ${({ $isClosable }) =>
    $isClosable ? 'flex-start' : 'center'};
  ${({ onClick }) => (onClick ? 'cursor: pointer;' : '')}
`;
